import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { productsAPI } from '../api/products';
import { Category } from '../types';

const CollectionsPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await productsAPI.getCategories();
        if (res.success && res.data) setCategories(res.data);
      } catch {
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="bg-light py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-widest uppercase text-nude mb-3">
            Explore
          </p>
          <h1 className="font-serif text-5xl text-primary">Collections</h1>
          <p className="text-xs text-nude tracking-wide mt-4 max-w-md mx-auto leading-relaxed">
            Discover our curated collections, each piece crafted with
            intention and timeless elegance.
          </p>
        </motion.div>
      </section>

      {/* Categories Grid */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton aspect-[4/5] rounded" />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-24">
            <p className="font-serif text-2xl text-primary mb-4">
              Collections Coming Soon
            </p>
            <p className="text-xs tracking-widest uppercase text-nude mb-8">
              We are curating something special
            </p>
            <Link to="/shop" className="btn-primary">
              Shop All
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/shop?category=${category.slug}`}
                  className="block group"
                >
                  <div className="aspect-[4/5] bg-light overflow-hidden relative">
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-light to-nude/30" />
                    )}
                    <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/30 transition-colors duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="bg-white/90 p-4">
                        <h2 className="font-serif text-2xl text-primary mb-1">
                          {category.name}
                        </h2>
                        {category.description && (
                          <p className="text-xs text-nude tracking-wide line-clamp-2">
                            {category.description}
                          </p>
                        )}
                        <div className="flex items-center space-x-2 mt-3 text-xs tracking-widest uppercase text-primary">
                          <span>Explore</span>
                          <ArrowRight size={12} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Featured Banner */}
      <section className="bg-primary py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto px-6"
        >
          <p className="text-xs tracking-widest uppercase text-nude mb-4">
            New Season
          </p>
          <h2 className="font-serif text-4xl text-white mb-6">
            The Latest Collection
          </h2>
          <p className="text-xs text-nude tracking-wide leading-relaxed mb-10">
            Explore our newest arrivals, crafted for the modern individual
            who appreciates timeless luxury.
          </p>
          <Link
            to="/shop?sort=newest"
            className="inline-block bg-white text-primary px-12 py-4 text-xs tracking-widest uppercase hover:bg-nude transition-colors"
          >
            Shop New Arrivals
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default CollectionsPage;